import React, { useState } from "react";
import {
  Box,
  ClickAwayListener,
  Dialog,
  DialogContent,
  Typography,
  TextField,
  IconButton,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import clsx from "clsx";
import { Button } from "components/UI";
import { FunctionComponent } from "react";
import { reduceResolution, wbp } from "Theme";
import { formatPhoneNumberInput, extractDigits } from "utils/PhoneUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-light-svg-icons";

interface AddPhoneNumberPopUpProps {
  onClose: () => void;
  onSave: (phoneNumber: string) => void;
  saving?: boolean;
  apiError?: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      "& .MuiDialog-paper": {
        boxShadow: "0px 10px 40px #00000029",
        [theme.breakpoints.down(wbp)]: {
          width: `${reduceResolution(500)}px`,
          minWidth: `${reduceResolution(500)}px`,
          borderRadius: `${reduceResolution(21)}px`,
        },
        [theme.breakpoints.up(wbp)]: {
          width: "500px",
          minWidth: "500px",
          borderRadius: "21px",
        },
      },

      "& .MuiDialogContent-root": {
        overflowY: "hidden",
        paddingTop: 0,
      },
    },

    boxContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      position: "relative",
      [theme.breakpoints.down(wbp)]: {
        paddingLeft: `${reduceResolution(40)}px`,
        paddingRight: `${reduceResolution(40)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        paddingLeft: "40px",
        paddingRight: "40px",
      },
    },

    dialogContainer: {
      width: "100%",
      "& .MuiDialog-paper": {
        overflowY: "hidden",
      },
      display: "flex",
      flexDirection: "column",
      padding: 0,
    },

    closeButton: {
      position: "absolute",
      padding: 0,
      [theme.breakpoints.down(wbp)]: {
        top: `${reduceResolution(16)}px`,
        right: `${reduceResolution(16)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        top: "16px",
        right: "16px",
      },
    },

    closeIcon: {
      color: "#222222",
      [theme.breakpoints.down(wbp)]: {
        fontSize: `${reduceResolution(20)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        fontSize: "20px",
      },
    },

    boxButtons: {
      borderTop: "1px solid #DDDDDD",
      display: "flex",
      justifyContent: "flex-end",
      [theme.breakpoints.down(wbp)]: {
        paddingBottom: `${reduceResolution(16)}px`,
        paddingTop: `${reduceResolution(16)}px`,
        paddingRight: `${reduceResolution(40)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        paddingBottom: "16px",
        paddingTop: "16px",
        paddingRight: "40px",
      },
    },

    buttonsContainer: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "8px",
    },

    button: {
      [theme.breakpoints.down(wbp)]: {
        width: `${reduceResolution(120)}px`,
        height: `${reduceResolution(47)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        width: "120px",
        height: "47px",
      },
    },

    title: {
      fontWeight: 600,
      textAlign: "left",
      [theme.breakpoints.down(wbp)]: {
        fontSize: `${reduceResolution(24)}px`,
        lineHeight: `${reduceResolution(30)}px`,
        paddingTop: `${reduceResolution(32)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        fontSize: "24px",
        lineHeight: "30px",
        paddingTop: "32px",
      },
    },

    description: {
      textAlign: "left",
      color: "#222222",
      [theme.breakpoints.down(wbp)]: {
        fontSize: `${reduceResolution(16)}px`,
        lineHeight: `${reduceResolution(24)}px`,
        paddingTop: `${reduceResolution(12)}px`,
        paddingBottom: `${reduceResolution(24)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        fontSize: "16px",
        lineHeight: "24px",
        paddingTop: "12px",
        paddingBottom: "24px",
      },
    },

    formContainer: {
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down(wbp)]: {
        gap: `${reduceResolution(16)}px`,
        paddingBottom: `${reduceResolution(32)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        gap: "16px",
        paddingBottom: "32px",
      },
    },

    inputLabel: {
      fontWeight: 500,
      color: "#222222",
      marginBottom: "8px",
      [theme.breakpoints.down(wbp)]: {
        fontSize: `${reduceResolution(14)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        fontSize: "14px",
      },
    },

    requiredAsterisk: {
      color: "#EAB464",
      marginLeft: "2px",
    },

    textField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: "30px",
        [theme.breakpoints.down(wbp)]: {
          height: `${reduceResolution(48)}px`,
        },
        [theme.breakpoints.up(wbp)]: {
          height: "48px",
        },
      },
      "& .MuiOutlinedInput-input": {
        [theme.breakpoints.down(wbp)]: {
          fontSize: `${reduceResolution(16)}px`,
          paddingLeft: `${reduceResolution(20)}px`,
          paddingRight: `${reduceResolution(20)}px`,
        },
        [theme.breakpoints.up(wbp)]: {
          fontSize: "16px",
          paddingLeft: "20px",
          paddingRight: "20px",
        },
      },
    },

    errorText: {
      color: "#EA6464",
      [theme.breakpoints.down(wbp)]: {
        fontSize: `${reduceResolution(12)}px`,
        marginTop: `${reduceResolution(4)}px`,
      },
      [theme.breakpoints.up(wbp)]: {
        fontSize: "12px",
        marginTop: "4px",
      },
    },
  })
);

export const AddPhoneNumberPopUp: FunctionComponent<AddPhoneNumberPopUpProps> = (
  props
) => {
  const classes = useStyles();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [confirmPhoneNumber, setConfirmPhoneNumber] = useState("");
  const [errors, setErrors] = useState<{ phone?: string; confirm?: string }>({});

  const saving = props.saving || false;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumberInput(e.target.value);
    setPhoneNumber(formatted);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  };

  const handleConfirmPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumberInput(e.target.value);
    setConfirmPhoneNumber(formatted);
    setErrors((prev) => ({ ...prev, confirm: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: { phone?: string; confirm?: string } = {};

    const phoneDigits = extractDigits(phoneNumber);
    const confirmDigits = extractDigits(confirmPhoneNumber);

    if (!phoneDigits) {
      newErrors.phone = "Phone number is required";
    } else if (phoneDigits.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (!confirmDigits) {
      newErrors.confirm = "Please confirm the phone number";
    } else if (phoneDigits !== confirmDigits) {
      newErrors.confirm = "Phone numbers do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      const digits = extractDigits(phoneNumber);
      // Send in +1 format to backend
      props.onSave(`+1${digits}`);
    }
  };

  const handleDiscard = () => {
    if (!saving) {
      props.onClose();
    }
  };

  const contentView = (
    <>
      <Box className={classes.boxContainer}>
        <IconButton className={classes.closeButton} onClick={handleDiscard} disabled={saving}>
          <FontAwesomeIcon icon={faTimes} className={classes.closeIcon} />
        </IconButton>
        <Typography className={classes.title}>Add Phone Number</Typography>
        <Typography className={classes.description}>
          Please make sure the number is entered correctly.
        </Typography>
        <Box className={classes.formContainer}>
          <Box>
            <Typography className={classes.inputLabel}>
              Phone Number<span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="(XXX) XXX-XXXX"
              value={phoneNumber}
              onChange={handlePhoneChange}
              error={!!errors.phone || !!props.apiError}
              className={classes.textField}
              inputProps={{ maxLength: 14 }}
              disabled={saving}
            />
            {errors.phone && (
              <Typography className={classes.errorText}>{errors.phone}</Typography>
            )}
            {props.apiError && !errors.phone && (
              <Typography className={classes.errorText}>{props.apiError}</Typography>
            )}
          </Box>
          <Box>
            <Typography className={classes.inputLabel}>
              Confirm Phone Number<span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="(XXX) XXX-XXXX"
              value={confirmPhoneNumber}
              onChange={handleConfirmPhoneChange}
              error={!!errors.confirm}
              className={classes.textField}
              inputProps={{ maxLength: 14 }}
              disabled={saving}
            />
            {errors.confirm && (
              <Typography className={classes.errorText}>
                {errors.confirm}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box className={classes.boxButtons}>
        <Box className={classes.buttonsContainer}>
          <Button
            label="Discard"
            loading={false}
            onClick={handleDiscard}
            variant="primary"
            size="medium"
            color="black"
            className={classes.button}
            disabled={saving}
          />
          <Button
            label="Save"
            loading={saving}
            onClick={handleSave}
            variant="primary"
            size="medium"
            color="green"
            className={classes.button}
            disabled={saving}
          />
        </Box>
      </Box>
    </>
  );

  return (
    <Box>
      <Dialog open={true} className={clsx(classes.container)}>
        <ClickAwayListener onClickAway={handleDiscard}>
          <DialogContent className={classes.dialogContainer}>
            {contentView}
          </DialogContent>
        </ClickAwayListener>
      </Dialog>
    </Box>
  );
};

export default AddPhoneNumberPopUp;
